import React, { ReactNode } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import BackButton from 'flow/components/ui-components/BackButton';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectChannelName,
    selectLastTypingUserName,
} from 'flow/selectors/channel-selectors';
import { ChannelKey, ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { getIdFromEntityKey } from 'flow/utils';

interface Props {
    channelKey: ChannelKey;
    chatSessionKey: ChatSessionKey;
    children: ReactNode;
    footer: ReactNode;
}

const MessagesPage = ({
    channelKey,
    chatSessionKey,
    children,
    footer,
}: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();

    const channelName = useAppSelector((state) =>
        selectChannelName(state, channelKey),
    );

    const typingUserName = useAppSelector((state) =>
        selectLastTypingUserName(state, channelKey, chatSessionKey),
    );

    return (
        <div className="badge-bg @container/messages flex size-full flex-col overflow-hidden bg-white">
            <header className="flex h-16 w-full shrink-0 items-center justify-center border-b px-4 font-bold">
                <div className="flex flex-1 items-center justify-start">
                    <BackButton
                        onClick={() =>
                            history.push(
                                generatePath(routes['pbx.channels.entityKey'], {
                                    entityKey: channelKey,
                                }),
                            )
                        }
                    />

                    {typingUserName ? (
                        <div>
                            {typingUserName} {i18next.t('chat.istyping')}
                            {'...'}
                        </div>
                    ) : null}
                </div>

                {`${
                    channelName || i18next.t('chat')
                } (#${getIdFromEntityKey(chatSessionKey)})`}

                <div className="flex flex-1 justify-end">
                    <IconButton
                        tooltipLabel={i18next.t('settings')}
                        onClick={() =>
                            history.push(
                                generatePath(
                                    routes[
                                        'pbx.channels.channelKey.chatSessionKey.settings'
                                    ],
                                    { channelKey, chatSessionKey },
                                ),
                            )
                        }
                    >
                        <Icon iconKey="setting_menu" />
                    </IconButton>
                </div>
            </header>

            {children}

            <footer className="flex flex-col items-center bg-white px-4 pb-3">
                {footer}
            </footer>
        </div>
    );
};

export default MessagesPage;
